import React, { useState } from "react";
import {
  Card,
  Box,
  Typography,
  useMediaQuery,
  IconButton,
  TextField,
  styled
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { ReactComponent as Delete } from "../../assets/delete.svg";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import {
  deleteIngredientsCustomOption,
  updateIngredientsOption
} from "../../api/diaryApi";
import useThemeColor from "../../utils/useTheme";
import { getCurrentUserDiaries } from "../../features/diary/diarySlice";
import { useDispatch } from "react-redux";

const StyledTextFieldContainer = styled(Box)(({ theme }) => ({
  position: "relative"
  // margin: theme.spacing(4, 0)
}));

const StyledTextField = styled(TextField)(({ styles }) => ({
  input: {
    color: styles.color,
    "-webkit-text-fill-color": `${styles.color} !important`
  },
  "& .MuiOutlinedInput-root": {
    "& input[type=number]": {
      "-moz-appearance": "textfield"
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0
    },
    "& fieldset": {
      borderColor: styles.lightGreyColor
    },
    "&:hover fieldset": {
      borderColor: styles.color
    },
    "&.Mui-focused fieldset": {
      borderColor: styles.color
    },
    "&.Mui-disabled fieldset": {
      borderColor: `${styles.lightGreyColor} !important`
    },
    "&.Mui-disabled": {
      background: styles.lightGreyColor,
      color: styles.color
    }
  }
}));

const CustomRadio = ({
  label,
  setIsDiaryRefreshNeeded,
  type,
  checked,
  onChange,
  id,
  userId,
  value,
  styles,
  levelId
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isEditing, setIsEditing] = useState(false);
  const stylesTheme = useThemeColor();
  const [editedLabel, setEditedLabel] = useState(label);
  const dispatch = useDispatch();
  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSaveClick = async (e) => {
    e.stopPropagation();
    if (editedLabel) {
      // Add your API call here to save the edited label
      await updateIngredientsOption(id, editedLabel);
      setIsDiaryRefreshNeeded(true);
      dispatch(getCurrentUserDiaries({ levelId }));
    }
    setIsEditing(false);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    await deleteIngredientsCustomOption(id);
    setIsDiaryRefreshNeeded(true);
  };

  const handleInputChange = (e) => {
    e.stopPropagation();
    setEditedLabel(e.target.value);
  };

  // Don't render empty items unless they're being edited
  if (!label && !isEditing && type === "STATIC") {
    return (
      <Box onClick={() => onChange(value)} sx={{ cursor: "pointer" }}>
        <Card
          sx={{
            backgroundColor: styles.lightGreyColor,
            "&:hover": {
              backgroundColor: styles.disabledColor,
              color: styles.background
            },
            transition: "background-color 0.3s",
            padding: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: "10px"
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: checked ? "#368AD0" : "transparent",
                border: checked ? "none" : "2px solid grey"
              }}
            />
            <IconButton
              onClick={handleEditClick}
              sx={{
                fontSize: "16px",
                // padding: "11px",
                borderRadius: "4px",
                background: "#368AD0",
                svg: {
                  path: {
                    fill: "#FCFCFC"
                  }
                },
                "&:hover": {
                  background: "#64AEDD"
                }
              }}
            >
              <EditIcon fontSize="16px" />
            </IconButton>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      onClick={() => !isEditing && onChange(value)}
      sx={{ cursor: "pointer" }}
    >
      <Card
        sx={{
          backgroundColor: styles.lightGreyColor,
          "&:hover": {
            backgroundColor: styles.disabledColor,
            color: styles.background
          },
          transition: "background-color 0.3s",
          padding: "16px", // Change this line to be consistent
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px"
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: checked ? "#368AD0" : "transparent",
              border: checked ? "none" : "2px solid grey"
            }}
          >
            {checked && (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "white",
                  borderRadius: "50%"
                }}
              />
            )}
          </Box>
          {isEditing ? (
            <StyledTextFieldContainer>
              <StyledTextField
                styles={stylesTheme}
                fullWidth
                variant="outlined"
                name="editedLabel"
                autoFocus
                // placeholder="Enter your Full name"
                value={editedLabel}
                onChange={handleInputChange}
                onClick={(e) => e.stopPropagation()}
                margin="normal"
              />
            </StyledTextFieldContainer>
          ) : (
            // <TextField
            //   value={editedLabel}
            //   onChange={handleInputChange}
            //   onClick={(e) => e.stopPropagation()}
            //   size="small"
            //   sx={{
            //     color: `${stylesTheme.color} !important`
            //   }}
            //   autoFocus
            // />
            <Typography
              fontSize={isMobile ? "15px" : "15px"}
              color={styles.color}
              variant="body1"
            >
              {label}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {type === "STATIC" && userId && (
            <IconButton
              onClick={isEditing ? handleSaveClick : handleEditClick}
              sx={{
                fontSize: "16px",
                borderRadius: "4px",
                background: "#368AD0",
                svg: {
                  path: {
                    fill: "#FCFCFC"
                  }
                },
                "&:hover": {
                  background: "#64AEDD"
                }
              }}
            >
              {isEditing ? (
                <CheckIcon fontSize="16px" />
              ) : (
                <EditIcon fontSize="16px" />
              )}
            </IconButton>
          )}
          {(
            userId
              ? ["STATIC", "CUSTOM"].includes(type) && userId && value
              : type === "CUSTOM"
          ) ? (
            <IconButton
              onClick={handleDelete}
              sx={{
                fontSize: "16px",
                borderRadius: "4px",
                background: "#FF4000",
                svg: {
                  path: {
                    fill: "#FCFCFC"
                  }
                },
                "&:hover": {
                  background: "#FF4000"
                }
              }}
            >
              <Delete />
            </IconButton>
          ) : (
            /* Add placeholder box when no icons are present */
            <Box sx={{ width: 32, height: 32 }} />
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default CustomRadio;
